import type {
  ChannelDto,
  LiveEventDto,
  ProgrammeChannelDto,
  SearchProgrammeDto,
  SearchSectionDto,
  SearchSectionType,
  TvShowDto,
  VodDto,
} from '@bldsoft/cappie/api/wbs'
import type {
  EpgProgramme,
  HybridSearchPayload,
  SearchSection,
} from '@setplex/tria-api'

import { toDate } from '../../tools'
import { formatLiveEvent } from '../live-events/live-events.format'
import { formatChannel } from '../tv/tv.format'
import { formatShow } from '../tvshows/tvshows.format'
import { formatVod } from '../vods/vods.format'

export type SearchSectionItemDto = NonNullable<
  SearchSectionDto['items']
>[number]

/**
 * The sections the app renders: backend section type → the value the `sections`
 * query parameter expects and the section key the client already groups by.
 * External content is left out on purpose (Movie of the Night keeps coming from
 * `GET /v3/movienight/search`), and `coming-up` has no design yet. Requesting a
 * value the backend does not know answers 400, so both sides live in one table.
 */
const REQUESTED_SECTIONS: Partial<
  Record<SearchSectionType, { query: string; key: SearchSection }>
> = {
  EVENTS: { query: 'events', key: 'LIVE_EVENTS' },
  WATCH_NOW: { query: 'watch-now', key: 'WATCH_NOW' },
  CATCHUPS: { query: 'catchups', key: 'CATCHUPS' },
  AVAILABLE_CONTENT: { query: 'available-content', key: 'AVAILABLE' },
}

export const REQUESTED_SEARCH_SECTIONS = Object.values(REQUESTED_SECTIONS)
  .map(({ query }) => query)
  .join(',')

const ONE_SECOND_MS = 1000

const toEpochMs = (
  epochSecond: number | undefined,
  isoDate: string | undefined
): number | undefined => {
  if (epochSecond != null) return epochSecond * ONE_SECOND_MS

  // WBS also sends date-times without an offset, which `Date.parse` would read
  // as local time; `toDate` pins those to UTC
  const parsed = toDate(isoDate)?.getTime()
  return parsed == null || Number.isNaN(parsed) ? undefined : parsed
}

export const formatSearchProgramme = (
  programme: SearchProgrammeDto | undefined
): EpgProgramme | undefined => {
  if (programme == null) return undefined

  return {
    programmeTitle: programme.title,
    startMs: toEpochMs(programme.startEpochSecond, programme.start),
    stopMs: toEpochMs(programme.stopEpochSecond, programme.stop),
  }
}

export const formatSectionItem = (
  item: SearchSectionItemDto
): HybridSearchPayload | null => {
  switch (item.contentType) {
    case 'LIVE_EVENT':
      return formatLiveEvent(item as LiveEventDto)
    case 'TV_CHANNEL':
      return formatChannel(item as ChannelDto)
    case 'TV_SHOW':
      return formatShow(item as TvShowDto)
    case 'VOD':
      return formatVod(item as VodDto)
    case 'TV_PROGRAMME_CHANNEL': {
      const { programme } = item as ProgrammeChannelDto
      return {
        ...formatChannel(item as ChannelDto),
        programme: formatSearchProgramme(programme),
      }
    }
    default:
      return null
  }
}

/**
 * Flattens the sectioned response into the list the app already consumes,
 * stamping every item with the section the backend put it in. Section order and
 * item order are the backend's (FR-1.5, FR-3.1); grouping downstream keys on
 * `section`, so the flat shape stays the single contract for both endpoints.
 *
 * The per-section `localizationKey` is ignored: section titles stay on the
 * client's own i18n keys (CR-409 LK-1…LK-4).
 */
export const formatSearchSections = (
  sections: readonly SearchSectionDto[]
): HybridSearchPayload[] =>
  sections.flatMap((section) => {
    const key =
      section.type == null ? undefined : REQUESTED_SECTIONS[section.type]?.key
    if (key == null) return []

    return (section.items ?? []).reduce<HybridSearchPayload[]>((acc, item) => {
      const formatted = formatSectionItem(item)
      if (formatted != null) acc.push({ ...formatted, section: key })
      return acc
    }, [])
  })
